/**
 * MoreSheet — slide-up bottom sheet triggered from BottomTabBar "More".
 *
 * Lists the secondary destinations that didn't fit into the 5-tab bar:
 *   Primary section:
 *     - Properties      -> /properties
 *     - Trends          -> /dashboard
 *     - Saved Filters   -> /saved (anchor: filters)  // placeholder until PR-3
 *     - Notifications   -> /notifications
 *     - Log out         -> calls onLogout()
 *   Screens section (the four specialty screens from Navbar):
 *     - Big Cheap Acres -> /screens/big-cheap-acreage
 *     - Stale           -> /screens/stale-acreage
 *     - Mineral Adjacent -> /screens/mineral-adjacent
 *     - Buildable Retreat -> /screens/buildable-retreat
 *
 * Behavior:
 *   - `open` prop controls visibility (parent owns the state).
 *   - Tapping a link OR the backdrop calls `onClose` so the sheet
 *     dismisses on the same gesture that triggered the navigation.
 *   - Escape key dismisses while open.
 *   - Body scroll is locked while open so the page underneath doesn't
 *     bounce-scroll on iOS.
 *   - Backdrop is rendered even when closed but with opacity 0 +
 *     pointer-events:none so we can transition rather than pop in.
 *
 * Out of scope for PR-2: the "Saved Filters" item points at /saved
 * for now; PR-3 will introduce a dedicated saved-filters surface. Log
 * out is wired now so the user has a way to exit the phone UX (the
 * top navbar's logout button is in the hamburger that PR-2 removes
 * on phone).
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PRIMARY_ITEMS = [
  { key: 'properties',    label: 'Properties',     path: '/properties' },
  { key: 'trends',        label: 'Trends',         path: '/dashboard' },
  { key: 'saved-filters', label: 'Saved Filters',  path: '/saved' },
  { key: 'notifications', label: 'Notifications',  path: '/notifications' },
];

const SCREEN_ITEMS = [
  { key: 'big-cheap',    label: 'Big Cheap Acres',    path: '/screens/big-cheap-acreage' },
  { key: 'stale',        label: 'Stale',              path: '/screens/stale-acreage' },
  { key: 'mineral',      label: 'Mineral Adjacent',   path: '/screens/mineral-adjacent' },
  { key: 'retreat',      label: 'Buildable Retreat',  path: '/screens/buildable-retreat' },
];

function MoreSheet({ open, onClose, onLogout, isAuthenticated }) {
  // Escape-to-dismiss + body-scroll lock while the sheet is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const handleLogoutClick = () => {
    onClose();
    if (typeof onLogout === 'function') onLogout();
  };

  return (
    <div
      className={`more-sheet-root ${open ? 'more-sheet-root--open' : ''}`}
      data-testid="more-sheet-root"
      aria-hidden={!open}
    >
      {/* Backdrop — clicking it closes the sheet. role=presentation
          keeps screen readers from announcing it as interactive
          (the sheet itself is the dialog). */}
      <div
        className="more-sheet-backdrop"
        onClick={onClose}
        role="presentation"
        data-testid="more-sheet-backdrop"
      />

      <div
        className="more-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="More navigation"
        data-testid="more-sheet"
      >
        {/* Drag handle — pure decoration but signals "swipe-down to
            dismiss" affordance (the swipe gesture itself is a P2
            polish item). */}
        <div className="more-sheet-handle" aria-hidden="true" />

        <ul className="more-sheet-list">
          {PRIMARY_ITEMS.map((item) => (
            <li key={item.key}>
              <Link
                to={item.path}
                className="more-sheet-item"
                onClick={onClose}
                data-testid={`more-sheet-item-${item.key}`}
              >
                {item.label}
              </Link>
            </li>
          ))}
          {isAuthenticated && (
            <li>
              <button
                type="button"
                className="more-sheet-item more-sheet-item--logout"
                onClick={handleLogoutClick}
                data-testid="more-sheet-item-logout"
              >
                Log out
              </button>
            </li>
          )}
        </ul>

        <div className="more-sheet-section-divider">
          <span>Screens</span>
        </div>

        <ul className="more-sheet-list">
          {SCREEN_ITEMS.map((item) => (
            <li key={item.key}>
              <Link
                to={item.path}
                className="more-sheet-item"
                onClick={onClose}
                data-testid={`more-sheet-item-${item.key}`}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default MoreSheet;
